import { useRef, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'frosted';
};

export function Button({ variant = 'primary', style, children, ...rest }: ButtonProps) {
  const variants = {
    primary: { background: 'var(--white)', color: '#0a0a0a', border: '1px solid transparent' },
    frosted: {
      background: 'rgba(255,255,255,0.07)',
      color: 'var(--text)',
      border: '1px solid var(--border)',
      backdropFilter: 'blur(8px)',
    },
  } as const;
  return (
    <button
      style={{
        width: '100%',
        padding: '14px 16px',
        borderRadius: 12,
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        opacity: rest.disabled ? 0.6 : 1,
        ...variants[variant],
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function TextField({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginBottom: 6 }}>
        {label}
      </span>
      <input
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 10,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          color: 'var(--text)',
          fontSize: 16,
        }}
        {...rest}
      />
    </label>
  );
}

export function TextArea({
  label,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginBottom: 6 }}>
        {label}
      </span>
      <textarea
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 10,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          color: 'var(--text)',
          fontSize: 16,
          resize: 'vertical',
          minHeight: 76,
          fontFamily: 'inherit',
        }}
        {...rest}
      />
    </label>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  return children ? (
    <p style={{ color: '#e5736f', fontSize: 13, margin: '2px 0 12px' }}>{children}</p>
  ) : null;
}

/** Honeypot field — hidden from humans; if a bot fills it we abort the submit. */
export function Honeypot({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="text"
      name="company"
      tabIndex={-1}
      autoComplete="off"
      aria-hidden="true"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
    />
  );
}

/** Full-screen dark layout for the auth (pre-login) screens. */
export function AuthScreen({
  children,
  transparent = false,
}: {
  children: ReactNode;
  transparent?: boolean;
}) {
  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        background: transparent
          ? 'transparent'
          : 'radial-gradient(120% 80% at 50% 0%, #161616 0%, #0a0a0a 60%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '32px 24px',
        maxWidth: 'var(--maxw)',
        margin: '0 auto',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Full-bleed looping login background video with a poster fallback and a legibility scrim.
 * Browsers block autoplay WITH sound, so it autoplays muted and the speaker button unmutes
 * it on tap. The sound button is a sibling (not inside the z-index:-1 layer) so it stays tappable.
 */
export function VideoBackdrop() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) void v.play();
  };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: -1, background: 'var(--bg)' }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          poster="/video/login-poster.jpg"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        >
          <source src="/video/flowers-stable.mp4" type="video/mp4" />
        </video>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.9) 100%)',
          }}
        />
      </div>
      <button
        type="button"
        onClick={toggleSound}
        aria-label={muted ? 'Unmute video' : 'Mute video'}
        style={{
          position: 'fixed',
          top: 14,
          right: 16,
          zIndex: 10,
          width: 40,
          height: 40,
          borderRadius: 999,
          background: 'rgba(0,0,0,0.45)',
          border: '1px solid var(--border)',
          color: 'var(--white)',
          fontSize: 16,
          backdropFilter: 'blur(6px)',
        }}
      >
        {muted ? '🔇' : '🔊'}
      </button>
    </>
  );
}
